<script setup lang="ts">
import { useCreateReport } from "@/api/report-query";
import { useSnackbarStore } from "@/stores";
import { mdiTextBoxPlusOutline } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { CreateReport, UserScope } from "@soft-db/monitoring-api-client";
import { FormField, Icon, InputGroup } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import * as z from "zod";
import AppDialog from "../common/AppDialog.vue";

const dialog = defineModel<boolean>("dialog");
const props = defineProps<{
	scope: UserScope;
}>();
const emit = defineEmits<{
	(e: "created", id: string): void;
}>();

const { t } = useI18n();
const snackbarStore = useSnackbarStore();

function close() {
	clear();
	dialog.value = false;
}

const reportCreation = useCreateReport();
const { useField, handleSubmit, clear } = useForm<CreateReport>();

const title = useField("title", { schema: z.string().trim().min(1) });
const description = useField("description", { initialValue: "" });

const createReport = handleSubmit(async (form) => {
	try {
		const newReport = await reportCreation.mutateAsync({
			scope: props.scope,
			data: form,
		});

		snackbarStore.openOnMessage(t("snackbar.reportCreated"));
		emit("created", newReport.id);
		close();
	} catch (e) {
		snackbarStore.openOnError(t("snackbar.internalServerError"));
		throw e;
	}
});
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@close="close"
		@click:action="createReport"
	>
		<template #title>
			<Icon :d="mdiTextBoxPlusOutline" />
			<span>{{ t("actions.createReport") }}</span>
		</template>
		<template #content>
			<FormField class="w-full" :label="t('common.title')" :errors="title.errors.value">
				<InputGroup class="w-full">
					<input v-model="title.data.value" />
				</InputGroup>
			</FormField>
			<FormField class="w-full" :label="t('common.description')" :errors="description.errors.value">
				<InputGroup class="w-full">
					<input v-model="description.data.value" />
				</InputGroup>
			</FormField>
		</template>
	</AppDialog>
</template>
